import { useState } from "react";
import { api } from "../api";
import { ArrowRight, CheckCircle2, AlertTriangle, TrendingUp } from "lucide-react";
import SEO from "../components/SEO";

export default function SimulatoreSostenibilita() {
  const [form, setForm] = useState({
    ebitda_base_eur_m: 1.5, purchase_price_eur_m: 6,
    equity_buyer_pct: 10, vendor_loan_pct: 30, vendor_loan_years: 7, vendor_loan_rate_pct: 4,
    bank_debt_pct: 50, bank_debt_years: 5, bank_debt_rate_pct: 6,
    earn_out_pct: 10, rollover_seller_pct: 0, guarantee_pct: 60,
    growth_scenarios_pct: [-15, 0, 15],
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  const total = form.equity_buyer_pct + form.vendor_loan_pct + form.bank_debt_pct + form.earn_out_pct + form.rollover_seller_pct;

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const r = await api.post("/platform/dscr", form);
      setResult(r.data);
      setStatus("done");
      setTimeout(() => window.scrollTo({ top: 900, behavior: "smooth" }), 100);
    } catch { setStatus("error"); }
  };

  return (
    <main data-testid="dscr-page">
      <SEO
        title="Simulatore di sostenibilità"
        description="La cassetta zero-equity per l'acquisizione PMI: vendor loan, earn-out, affitto-riscatto, garanzia pubblica, rollover del venditore. Simulatore DSCR con tre scenari di crescita per capire se il deal regge il debito."
        path="/simulatore-sostenibilita"
      />
      <section style={{ background: "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Simulatore di sostenibilità</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 980 }}>
            La cassetta<br />
            <span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>zero-equity.</span>{" "}
            Stress-tested.
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 760, opacity: 0.78, lineHeight: 1.7 }}>
            Vendor loan, earn-out, affitto-riscatto, garanzia pubblica, rollover del venditore. Combinali e vedi in tempo reale se i flussi reggono il debito (DSCR) in tre scenari di crescita.
          </p>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--muted)" }}>
        <div className="container-x max-w-4xl">
          <form onSubmit={submit} className="card-flat p-8 md:p-10" data-testid="dscr-form">
            <h3 className="serif-display" style={{ fontSize: 24 }}>Struttura del deal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
              <Range label={`EBITDA base (€M): ${form.ebitda_base_eur_m}`} value={form.ebitda_base_eur_m} onChange={v => setForm({ ...form, ebitda_base_eur_m: v })} min={0.1} max={20} step={0.1} tid="d-ebitda" />
              <Range label={`Prezzo di acquisto (€M): ${form.purchase_price_eur_m}`} value={form.purchase_price_eur_m} onChange={v => setForm({ ...form, purchase_price_eur_m: v })} min={0.5} max={100} step={0.5} tid="d-price" />
            </div>

            <h3 className="serif-display mt-10" style={{ fontSize: 24 }}>Composizione del prezzo (deve fare 100%)</h3>
            <div className="mt-2 text-sm font-mono" style={{ color: Math.abs(total - 100) < 0.5 ? "#065F46" : "var(--terracotta)" }}>
              Totale attuale: {total}%
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
              <Range label={`Equity acquirente: ${form.equity_buyer_pct}%`} value={form.equity_buyer_pct} onChange={v => setForm({ ...form, equity_buyer_pct: v })} min={0} max={100} step={5} tid="d-eq" />
              <Range label={`Vendor loan: ${form.vendor_loan_pct}%`} value={form.vendor_loan_pct} onChange={v => setForm({ ...form, vendor_loan_pct: v })} min={0} max={70} step={5} tid="d-vl" />
              <Range label={`Debito bancario: ${form.bank_debt_pct}%`} value={form.bank_debt_pct} onChange={v => setForm({ ...form, bank_debt_pct: v })} min={0} max={80} step={5} tid="d-bd" />
              <Range label={`Earn-out: ${form.earn_out_pct}%`} value={form.earn_out_pct} onChange={v => setForm({ ...form, earn_out_pct: v })} min={0} max={50} step={5} tid="d-eo" />
              <Range label={`Rollover venditore: ${form.rollover_seller_pct}%`} value={form.rollover_seller_pct} onChange={v => setForm({ ...form, rollover_seller_pct: v })} min={0} max={30} step={5} tid="d-ro" />
              <Range label={`Garanzia pubblica (FGP): ${form.guarantee_pct}%`} value={form.guarantee_pct} onChange={v => setForm({ ...form, guarantee_pct: v })} min={0} max={100} step={10} tid="d-gar" />
            </div>

            <div className="mt-8 flex items-center gap-6">
              <button type="submit" disabled={status === "loading"} className="btn-primary" data-testid="d-submit">
                {status === "loading" ? "..." : "Calcola sostenibilità"} <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </div>
      </section>

      {result && (
        <section className="py-16 md:py-24" data-testid="dscr-result">
          <div className="container-x max-w-4xl">
            <div className={`p-8 md:p-10 flex items-start gap-4`} style={{ background: result.sustainable ? "#ECFDF5" : "#FEF2F2", border: `1px solid ${result.sustainable ? "#10B981" : "var(--terracotta)"}` }}>
              {result.sustainable
                ? <CheckCircle2 size={28} style={{ color: "#065F46", flexShrink: 0 }} />
                : <AlertTriangle size={28} style={{ color: "var(--terracotta)", flexShrink: 0 }} />}
              <div>
                <div className="serif-display" style={{ fontSize: 28 }}>
                  {result.sustainable ? "Struttura sostenibile" : "Struttura da rivedere"}
                </div>
                <p className="mt-2 text-sm" style={{ opacity: 0.85 }}>
                  Servizio del debito annuo: <b>€{(result.vendor_annuity_eur_m + result.bank_annuity_eur_m).toFixed(2)}M</b> · Vendor: €{result.vendor_annuity_eur_m}M · Banca: €{result.bank_annuity_eur_m}M
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-8" style={{ borderTop: "1px solid var(--border)", borderLeft: "1px solid var(--border)" }}>
              {result.scenarios.map((s, i) => (
                <div key={i} className="p-6 md:p-8" style={{ borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: i === 1 ? "var(--midnight)" : "var(--card)", color: i === 1 ? "var(--bone)" : "inherit" }}>
                  <div className="font-mono text-xs" style={{ color: i === 1 ? "var(--gold)" : "var(--terracotta)" }}>{s.label.toUpperCase()}</div>
                  <div className="serif-display mt-4" style={{ fontSize: 44, lineHeight: 1 }}>{s.dscr ?? "—"}</div>
                  <div className="text-xs font-mono mt-2" style={{ opacity: 0.7 }}>DSCR</div>
                  <div className="mt-6 chip" style={{ background: verdictColor(s.verdict), color: "var(--bone)", borderColor: "transparent" }}>{s.verdict}</div>
                  <div className="mt-6 text-xs" style={{ opacity: 0.7 }}>
                    EBITDA proiettato: €{s.ebitda_projected_eur_m}M
                  </div>
                </div>
              ))}
            </div>

            {result.warnings.length > 0 && (
              <div className="mt-10 p-6" style={{ background: "var(--muted)", borderLeft: "3px solid var(--terracotta)" }}>
                <div className="overline" style={{ color: "var(--terracotta)" }}>Segnali</div>
                <ul className="mt-3 space-y-2 text-sm">{result.warnings.map((w, i) => <li key={i}>· {w}</li>)}</ul>
              </div>
            )}
          </div>
        </section>
      )}
    </main>
  );
}

function Range({ label, value, onChange, min, max, step, tid }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input type="range" data-testid={tid} min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} style={{ width: "100%" }} />
    </div>
  );
}
function verdictColor(v) {
  return { sostenibile: "#065F46", borderline: "#B45309", fragile: "var(--terracotta)", insostenibile: "#7f1d1d" }[v] || "var(--midnight)";
}
